import { featureForPath } from '@/config/features';
import type { NavItem } from '@/config/navigation';
import { useAuthStore } from '@/stores/auth-store';

/** Bagian butir menu yang menentukan boleh-tidaknya ia tampil. */
export type NavAccess = Pick<NavItem, 'href' | 'permission' | 'notForClassTeacher' | 'graduatingOnly'>;

/**
 * Penentu apakah satu butir menu boleh tampil bagi pengguna ini.
 *
 * Dipakai bersama oleh sidebar dan stepper alur kerja, supaya keduanya
 * menyaring dengan aturan yang sama — langkah yang bisa diklik tapi halamannya
 * menolak sama menyesatkannya dengan menu yang tampil lalu menolak.
 */
export function useNavItemVisibility() {
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const hasFeature = useAuthStore((state) => state.hasFeature);
  const classTeacher = useAuthStore((state) => state.user?.classTeacher ?? null);

  return (item: NavAccess) => {
    if (item.permission && !hasPermission(item.permission)) {
      return false;
    }

    // Guru kelas: izin saja tidak cukup. Sebagian menu terbuka baginya hanya
    // karena layar akademiknya menumpang izin baca yang sama — lihat
    // `notForClassTeacher` & `graduatingOnly` di `@/config/navigation`.
    if (classTeacher) {
      if (item.notForClassTeacher) {
        return false;
      }
      if (item.graduatingOnly && !classTeacher.hasGraduatingGroup) {
        return false;
      }
    }

    // Modul yang tidak termasuk paket langganan lembaga tidak ditampilkan
    // sama sekali — menu yang terlihat lalu menolak saat diklik lebih
    // menjengkelkan daripada menu yang memang tidak ada. Fiturnya
    // ditentukan dari alamat halamannya; lihat `@/config/features`.
    const feature = featureForPath(item.href);
    return !feature || hasFeature(feature.key);
  };
}
